import React from "react";
import { Crown, Star, User } from "lucide-react";

/**
 * Service Level Badge Component
 * Shows the client's service level with appropriate styling
 */
export const ServiceLevelBadge = ({ planType, showPrice = false, size = "md" }) => {
  const plans = {
    estandar: {
      name: "ESTÁNDAR",
      price: "$19.99",
      bgClass: "bg-gray-100",
      textClass: "text-gray-700",
      borderClass: "border-gray-300",
      icon: User,
    },
    premium: {
      name: "PREMIUM",
      price: "$49.99",
      bgClass: "bg-blue-100",
      textClass: "text-blue-700",
      borderClass: "border-blue-300",
      icon: Star,
    },
    vip: {
      name: "VIP",
      price: "$99.99",
      bgClass: "bg-amber-100",
      textClass: "text-amber-700",
      borderClass: "border-amber-400",
      icon: Crown,
    },
    // Legacy mappings
    standard: {
      name: "ESTÁNDAR",
      price: "$19.99",
      bgClass: "bg-gray-100",
      textClass: "text-gray-700",
      borderClass: "border-gray-300",
      icon: User,
    },
    advanced: {
      name: "PREMIUM",
      price: "$49.99",
      bgClass: "bg-blue-100",
      textClass: "text-blue-700",
      borderClass: "border-blue-300",
      icon: Star,
    },
  };

  const plan = plans[planType] || plans.estandar;
  const Icon = plan.icon;

  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-2.5 py-1",
    lg: "text-base px-3 py-1.5",
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-semibold rounded-full border ${plan.bgClass} ${plan.textClass} ${plan.borderClass} ${sizeClasses[size]}`}
      data-testid={`service-level-badge-${planType}`}
    >
      <Icon className={size === "sm" ? "w-3 h-3" : size === "lg" ? "w-5 h-5" : "w-4 h-4"} />
      <span>{plan.name}</span>
      {showPrice && <span className="opacity-75">({plan.price})</span>}
    </span>
  );
};

/**
 * Service Level Card Component
 * Full card view for plan comparison in sales view
 */
export const ServiceLevelCard = ({ planType, selected, onSelect }) => {
  const plans = {
    estandar: {
      name: "ESTÁNDAR",
      price: "$1.99",
      responseTime: "3 a 7 días",
      processTime: "Hasta 9 semanas",
      priority: "Último en cola",
      bgGradient: "from-gray-50 to-gray-100",
      borderColor: "border-gray-300",
      accentColor: "bg-gray-500",
      benefits: [
        "Asesoría migratoria básica",
        "Portal de documentos",
        "Chat con asistente IA",
        "Notificaciones por email",
      ],
    },
    premium: {
      name: "PREMIUM",
      price: "$24.99",
      responseTime: "2 a 4 días",
      processTime: "4 a 5 semanas",
      priority: "Prioridad media",
      bgGradient: "from-blue-50 to-blue-100",
      borderColor: "border-blue-400",
      accentColor: "bg-blue-500",
      benefits: [
        "Todo lo del Estándar",
        "Revisión de documentos",
        "Simulador de entrevista",
        "Notificaciones WhatsApp",
        "Atención prioritaria",
      ],
    },
    vip: {
      name: "VIP ENTERPRISE",
      price: "$39.99",
      responseTime: "Máximo 24 horas",
      processTime: "Proceso acelerado",
      priority: "Máxima prioridad",
      bgGradient: "from-amber-50 to-amber-100",
      borderColor: "border-amber-400",
      accentColor: "bg-amber-500",
      benefits: [
        "Todo lo del Premium",
        "Asesor VIP dedicado",
        "Contacto inmediato",
        "Dashboard especial",
        "Atención 24/7",
        "Proceso ultra-acelerado",
      ],
    },
  };

  const plan = plans[planType] || plans.estandar;

  return (
    <div
      onClick={() => onSelect && onSelect(planType)}
      className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all ${
        selected
          ? `${plan.borderColor} shadow-lg scale-[1.02]`
          : "border-navy-200 hover:border-navy-300"
      } bg-gradient-to-br ${plan.bgGradient}`}
    >
      {planType === "vip" && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            RECOMENDADO
          </span>
        </div>
      )}

      <div className="text-center mb-4">
        <h3 className="font-bold text-xl text-navy-900">{plan.name}</h3>
        <div className="mt-2">
          <span className="text-3xl font-bold text-navy-900">{plan.price}</span>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <span className={`w-2 h-2 rounded-full ${plan.accentColor}`}></span>
          <span className="text-navy-600">Respuesta: {plan.responseTime}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className={`w-2 h-2 rounded-full ${plan.accentColor}`}></span>
          <span className="text-navy-600">Proceso: {plan.processTime}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className={`w-2 h-2 rounded-full ${plan.accentColor}`}></span>
          <span className="text-navy-600">{plan.priority}</span>
        </div>
      </div>

      <ul className="space-y-2">
        {plan.benefits.map((benefit, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-navy-700">
            <span className="text-emerald-500 mt-0.5">✓</span>
            {benefit}
          </li>
        ))}
      </ul>

      {selected && (
        <div className="absolute top-4 right-4">
          <span className={`w-6 h-6 rounded-full ${plan.accentColor} flex items-center justify-center text-white`}>
            ✓
          </span>
        </div>
      )}
    </div>
  );
};

export default ServiceLevelBadge;
